import { useMemo } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import useMarketPriceHistroyWindow from './use-market-price-histroy-window';

export interface MarketPriceHistoryPoint {
  t: number;
  p: number;
  v: number;
}

export interface MarketPriceHistoryResponse {
  history: MarketPriceHistoryPoint[];
}

export interface MarketPriceHistoryRequest {
  conditionId: string;
  market: string;
}

export interface MarketPriceHistoryCommonParams {
  fidelity?: number | string;
  startTime?: number;
  endTime?: number;
  interval?: string;
}

export interface MarketPriceHistoryResult extends MarketPriceHistoryResponse {
  key: string;
  request: MarketPriceHistoryRequest & MarketPriceHistoryCommonParams;
}

export interface UseMarketsPriceHistoryOptions {
  markets?: MarketPriceHistoryRequest[];
  market?: MarketPriceHistoryRequest;
  fidelity?: number | string;
  startTime?: string | number | null;
  endTime?: string | number | null;
  interval?: string;
  enabled?: boolean;
  swr?: SWRConfiguration<MarketPriceHistoryResult[], Error>;
}

export async function fetchMarketPriceHistory(
  request: MarketPriceHistoryRequest,
  commonParams: MarketPriceHistoryCommonParams = {},
): Promise<MarketPriceHistoryResult> {
  const searchParams = new URLSearchParams();

  searchParams.set('condition_id', request.conditionId);
  searchParams.set('market', request.market);
  searchParams.set('fidelity', String(commonParams.fidelity ?? 60));

  if (typeof commonParams.startTime === 'number') {
    searchParams.set('start_time', String(commonParams.startTime));
  }

  if (typeof commonParams.endTime === 'number') {
    searchParams.set('end_time', String(commonParams.endTime));
  }

  if (commonParams.interval) {
    searchParams.set('interval', commonParams.interval);
  }

  const response = await customFetch(`/api/market/prices-history?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch market price history');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch market price history: ${response.status}`);
  }

  const data = (await response.json()) as MarketPriceHistoryResponse;

  return {
    key: getRequestKey(request, commonParams),
    request: { ...request, ...commonParams },
    history: Array.isArray(data.history) ? data.history : [],
  };
}

export async function fetchMarketsPriceHistory(
  requests: MarketPriceHistoryRequest[],
  commonParams: MarketPriceHistoryCommonParams = {},
) {
  return Promise.all(requests.map((request) => fetchMarketPriceHistory(request, commonParams)));
}

export default function useMarketsPriceHistory(options: UseMarketsPriceHistoryOptions = {}) {
  const { enabled = true, swr } = options;
  const historyWindow = useMarketPriceHistroyWindow({
    startTime: options.startTime,
    endTime: options.endTime,
  });
  const commonParams = useMemo<MarketPriceHistoryCommonParams>(
    () => ({
      fidelity: options.fidelity ?? historyWindow.fidelity,
      startTime: historyWindow.startTimeSec,
      endTime: historyWindow.endTimeSec,
      interval: options.interval,
    }),
    [
      historyWindow.endTimeSec,
      historyWindow.fidelity,
      historyWindow.startTimeSec,
      options.fidelity,
      options.interval,
    ],
  );
  const requests = useMemo(
    () =>
      normalizeRequests(options.markets ?? (options.market ? [options.market] : []), commonParams),
    [commonParams, options.market, options.markets],
  );

  const swrKey =
    enabled && requests.length > 0
      ? [
          'markets-price-history',
          requests.map((request) => getRequestKey(request, commonParams)).join('|'),
        ]
      : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    MarketPriceHistoryResult[],
    Error
  >(swrKey, () => fetchMarketsPriceHistory(requests, commonParams), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000,
    ...swr,
  });

  const history = data?.[0]?.history ?? [];
  const historyByKey = useMemo(() => {
    const map: Record<string, MarketPriceHistoryResult> = {};
    for (const item of data ?? []) {
      map[item.key] = item;
    }
    return map;
  }, [data]);

  return {
    data: data ?? [],
    history,
    historyByKey,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeRequests(
  requests: MarketPriceHistoryRequest[],
  commonParams: MarketPriceHistoryCommonParams,
) {
  const seen = new Set<string>();

  return requests.filter((request) => {
    if (!request.conditionId || !request.market) return false;

    const key = getRequestKey(request, commonParams);
    if (seen.has(key)) return false;
    seen.add(key);

    return true;
  });
}

function getRequestKey(
  request: MarketPriceHistoryRequest,
  commonParams: MarketPriceHistoryCommonParams,
) {
  return [
    request.conditionId,
    request.market,
    commonParams.fidelity ?? 60,
    commonParams.startTime ?? '',
    commonParams.endTime ?? '',
    commonParams.interval ?? '',
  ].join(':');
}
